import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router-dom';

import { ROOTS } from 'src/routes/paths';

import { AuthGuard } from 'src/auth/guard';
import ModuleLayout from 'src/layouts/modules';
import { EntityProvider } from 'src/entity/context';
import GESDashboardLayout from 'src/layouts/dashboard/ges';
import DashboardAdminLayout from 'src/layouts/dashboard/admin';
import DiagRefDashboardLayout from 'src/layouts/dashboard/diag_ref';
import EngagementDashboardLayout from 'src/layouts/dashboard/engagement';
import {
  GESGuard,
  EntityGuard,
  DiagRefGuard,
  EngagementGuard,
  EntityAccessGuard,
} from 'src/entity/guard';

import { SplashScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

// Home
const ModulesPage = lazy(() => import('src/pages/entity/modules/index'));

// Settings
const SettingsIndexPage = lazy(() => import('src/pages/entity/settings/index'));
const AdministrationIndexPage = lazy(() => import('src/pages/entity/administration/users'));

// GES Pages
const DashboardIndexPage = lazy(() => import('src/pages/entity/modules/ges/dashboard/index'));
const InventoryIndexPage = lazy(() => import('src/pages/entity/modules/ges/inventory/index'));
const SimulationsIndexPage = lazy(() => import('src/pages/entity/modules/ges/simulations/index'));
const SimulationDetailsPage = lazy(
  () => import('src/pages/entity/modules/ges/simulations/details')
);
const InventoryDetailsPage = lazy(() => import('src/pages/entity/modules/ges/inventory/details'));

// Engagement Pages
const EngagementDashboardPage = lazy(
  () => import('src/pages/entity/modules/engagement/dashboard/index')
);

const EngagementPlanningPage = lazy(
  () => import('src/pages/entity/modules/engagement/planning/index')
);

const EngagementActionsPage = lazy(
  () => import('src/pages/entity/modules/engagement/actions/index')
);

const EngagementActionDetailsPage = lazy(
  () => import('src/pages/entity/modules/engagement/actions/details')
);

const EngagementActionSettingsPage = lazy(
  () => import('src/pages/entity/modules/engagement/settings/index')
);

// DiagRef Pages
const DiagRefDashboardPage = lazy(() => import('src/pages/entity/modules/diag_ref/index'));
const DiagRefDiagnostiquesPage = lazy(
  () => import('src/pages/entity/modules/diag_ref/diagnostiques')
);

// Reuse Pages
const ReuseIndexPage = lazy(() => import('src/pages/entity/modules/ges/reuse/index'));
const ReuseTablePage = lazy(() => import('src/pages/entity/modules/ges/reuse/table'));

// ----------------------------------------------------------------------

export const entityRoutes = [
  {
    path: ROOTS.ENTITY,
    element: (
      <AuthGuard>
        <EntityProvider>
          <EntityGuard>
            <Suspense fallback={<SplashScreen />}>
              <Outlet />
            </Suspense>
          </EntityGuard>
        </EntityProvider>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
      },
      {
        path: ':parent_entity_id',
        handle: {
          crumb: {
            text: 'parent_entity_name',
            disabled: true,
          },
        },
        children: [
          {
            index: true,
            element: <Navigate to="modules" replace />,
          },
          {
            path: 'modules',
            element: (
              <ModuleLayout>
                <ModulesPage />
              </ModuleLayout>
            ),
            handle: {
              crumb: {
                i18nKey: 'nav.menu.modules',
              },
            },
          },
          {
            path: 'administration',
            element: (
              <EntityAccessGuard>
                <DashboardAdminLayout>
                  <Suspense fallback={<SplashScreen />}>
                    <AdministrationIndexPage />
                  </Suspense>
                </DashboardAdminLayout>
              </EntityAccessGuard>
            ),
            handle: {
              crumb: {
                i18nKey: 'nav.menu.administration',
              },
            },
          },
          {
            path: ':entity_id',
            handle: {
              crumb: {
                text: 'entity_name',
                disabled: true,
              },
            },
            children: [
              {
                index: true,
                element: <Navigate to="modules" replace />,
              },
              {
                path: 'modules',
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.modules',
                  },
                },
                element: (
                  <ModuleLayout>
                    <ModulesPage />
                  </ModuleLayout>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'ges',
        element: (
          <GESGuard>
            <GESDashboardLayout>
              <Outlet />
            </GESDashboardLayout>
          </GESGuard>
        ),
        children: [
          {
            path: ':parent_entity_id',
            handle: {
              crumb: {
                text: 'parent_entity_name',
                disabled: true,
              },
            },
            children: [
              {
                path: 'dashboard',
                element: <DashboardIndexPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.dashboard',
                  },
                },
              },
              {
                path: 'inventory',
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.inventory',
                  },
                },
                children: [
                  {
                    index: true,
                    element: <InventoryIndexPage />,
                  },
                  {
                    path: ':inventory_id',
                    element: <InventoryDetailsPage />,
                    handle: {
                      crumb: {
                        text: 'inventory_name',
                      },
                    },
                  },
                ],
              },
              {
                path: 'settings',
                element: <SettingsIndexPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.settings',
                  },
                },
              },
              {
                path: ':entity_id?',
                handle: {
                  crumb: {
                    text: 'entity_name',
                    disabled: true,
                  },
                },
                children: [
                  {
                    path: 'dashboard',
                    element: <DashboardIndexPage />,
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.dashboard',
                      },
                    },
                  },
                  {
                    path: 'inventory',
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.inventory',
                      },
                    },
                    children: [
                      {
                        index: true,
                        element: <InventoryIndexPage />,
                      },
                      {
                        path: ':inventory_id',
                        element: <InventoryDetailsPage />,
                        handle: {
                          crumb: {
                            text: 'inventory_name',
                          },
                        },
                      },
                    ],
                  },
                  {
                    path: 'settings',
                    element: <SettingsIndexPage />,
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.settings',
                      },
                    },
                  },
                  {
                    path: 'simulations',
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.simulations',
                      },
                    },
                    children: [
                      {
                        index: true,
                        element: <SimulationsIndexPage />,
                      },
                      {
                        path: ':simulation_id',
                        element: <SimulationDetailsPage />,
                        handle: {
                          crumb: {
                            text: 'simulation_name',
                          },
                        },
                      },
                    ],
                  },
                  {
                    path: 'reuse',
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.reuse',
                      },
                    },
                    children: [
                      {
                        index: true,
                        element: <ReuseIndexPage />,
                      },
                      {
                        path: ':reuse_id/table',
                        element: <ReuseTablePage />,
                        handle: {
                          crumb: {
                            text: 'reuse_name',
                          },
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'engagement',
        element: (
          <EngagementGuard>
            <EngagementDashboardLayout>
              <Outlet />
            </EngagementDashboardLayout>
          </EngagementGuard>
        ),
        children: [
          {
            path: ':parent_entity_id',
            handle: {
              crumb: {
                text: 'parent_entity_name',
                disabled: true,
              },
            },
            children: [
              {
                path: 'dashboard',
                element: <EngagementDashboardPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.engagement.dashboard',
                  },
                },
              },
              {
                path: 'planning',
                element: <EngagementPlanningPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.engagement.planning',
                  },
                },
              },
              {
                path: 'actions',
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.engagement.actions',
                  },
                },
                children: [
                  {
                    index: true,
                    element: <EngagementActionsPage />,
                  },
                  {
                    path: ':action_id',
                    element: <EngagementActionDetailsPage />,
                    handle: {
                      crumb: {
                        text: 'action_name',
                      },
                    },
                  },
                ],
              },
              {
                path: 'settings',
                element: <EngagementActionSettingsPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.engagement.settings',
                  },
                },
              },
              {
                path: ':entity_id?',
                handle: {
                  crumb: {
                    text: 'entity_name',
                    disabled: true,
                  },
                },
                children: [
                  {
                    path: 'dashboard',
                    element: <EngagementDashboardPage />,
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.engagement.dashboard',
                      },
                    },
                  },
                  {
                    path: 'planning',
                    element: <EngagementPlanningPage />,
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.engagement.planning',
                      },
                    },
                  },
                  {
                    path: 'actions',
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.engagement.actions',
                      },
                    },
                    children: [
                      {
                        index: true,
                        element: <EngagementActionsPage />,
                      },
                      {
                        path: ':action_id',
                        element: <EngagementActionDetailsPage />,
                        handle: {
                          crumb: {
                            text: 'action_name',
                          },
                        },
                      },
                    ],
                  },
                  {
                    path: 'settings',
                    element: <EngagementActionSettingsPage />,
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.engagement.settings',
                      },
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'diag_ref',
        element: (
          <DiagRefGuard>
            <DiagRefDashboardLayout>
              <Outlet />
            </DiagRefDashboardLayout>
          </DiagRefGuard>
        ),
        children: [
          {
            path: ':parent_entity_id',
            handle: {
              crumb: {
                text: 'parent_entity_name',
                disabled: true,
              },
            },
            children: [
              {
                path: 'dashboard',
                element: <DiagRefDashboardPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.diag_ref.dashboard',
                  },
                },
              },
              {
                path: 'diagnostiques',
                element: <DiagRefDiagnostiquesPage />,
                handle: {
                  crumb: {
                    i18nKey: 'nav.menu.diag_ref.diagnostiques',
                  },
                },
              },
              {
                path: ':entity_id?',
                handle: {
                  crumb: {
                    text: 'entity_name',
                    disabled: true,
                  },
                },
                children: [
                  {
                    path: 'dashboard',
                    element: <DiagRefDashboardPage />,
                    handle: {
                      crumb: {
                        i18nKey: 'nav.menu.diag_ref.dashboard',
                      },
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
